/**
 * Document Service - Servicio de gestión de documentos
 */

import { ApiServiceBase } from '../core/ApiService';
import type {
  Document,
  DocumentFilters,
  DocumentWithSearchResults,
  SharedFilters,
  MentionSuggestionsResponse,
  SearchFilesResponse,
  DocumentSelectionResponse,
  ChunkWithContextResponse,
  DocumentChunksResponse,
  ChunkTablesResponse,
  DocumentTablesResponse,
  UnstructuredTablesResponse,
  MentionConfigResponse,
} from '../types/document';
import { authManager } from '../../../utils/authManager';
import logger from '../../../utils/simpleLogger';

export class DocumentService extends ApiServiceBase {
  /**
   * Obtiene documentos filtrados
   */
  async getFilteredDocuments(filters: {
    company?: string | string[];
    type?: string | string[];
    period?: string | string[];
    sector?: string | string[];
    search?: string;
    limit?: number;
    search_in_content?: boolean;
    scope?: 'shared' | 'private';
    sort?: string;
  }): Promise<{
    success: boolean;
    documents: Document[];
    count: number;
    filters_applied: {
      company?: string;
      document_type?: string;
      document_period?: string;
    };
    search?: string;
  }> {
    this._logFilteredDocumentsCall(filters);
    const params = this._buildFilterParams(filters);
    this._logSearchMode(filters.search_in_content);
    return this.request(`/documents/filtered?${params.toString()}`);
  }

  private _logFilteredDocumentsCall(filters: any): void {
    logger.info('📄 [API] getFilteredDocuments() called', {
      ...filters,
      search_in_content: filters.search_in_content,
      search_in_content_type: typeof filters.search_in_content,
    });
  }

  private _buildFilterParams(filters: {
    company?: string | string[];
    type?: string | string[];
    period?: string | string[];
    sector?: string | string[];
    search?: string;
    limit?: number;
    search_in_content?: boolean;
    scope?: 'shared' | 'private';
    sort?: string;
  }): URLSearchParams {
    const joinFilter = (v: string | string[] | undefined): string | undefined => {
      if (!v) return undefined;
      const arr = Array.isArray(v) ? v : [v];
      return arr.length > 0 ? arr.join(',') : undefined;
    };
    const params = new URLSearchParams();
    const companyStr = joinFilter(filters.company);
    if (companyStr) params.append('company', companyStr);
    const typeStr = joinFilter(filters.type);
    if (typeStr) params.append('type', typeStr);
    const periodStr = joinFilter(filters.period);
    if (periodStr) params.append('period', periodStr);
    const sectorStr = joinFilter(filters.sector);
    if (sectorStr) params.append('sector', sectorStr);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search_in_content === true) {
      params.append('search_in_content', 'true');
    }
    if (filters.scope) params.append('scope', filters.scope);
    if (filters.sort) params.append('sort', filters.sort);
    return params;
  }

  private _logSearchMode(searchInContent?: boolean): void {
    if (searchInContent === true) {
      logger.info('✨ [API] Fuzzy search enabled (search_in_content=true)');
    } else {
      logger.info('🔍 [API] Fuzzy search disabled', {
        search_in_content: searchInContent,
        isFalse: searchInContent === false,
        isUndefined: searchInContent === undefined,
      });
    }
  }

  /**
   * Obtiene todos los documentos del usuario
   */
  async getDocuments(filters?: { search?: string; search_in_content?: boolean }): Promise<{
    success: boolean;
    documents: Document[];
    count: number;
    search_in_content?: boolean;
  }> {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.search_in_content) params.append('search_in_content', 'true');

    logger.info('📄 [API] getDocuments() called', { filters });
    try {
      const response = await this.request(`/documents?${params.toString()}`);
      logger.info('📄 [API] getDocuments success', {
        count: (response as any).count,
      });
      return response as {
        success: boolean;
        documents: Document[];
        count: number;
        search_in_content?: boolean;
      };
    } catch (error) {
      logger.error('❌ [API] getDocuments failed', error);
      throw error;
    }
  }

  /**
   * Obtiene un documento específico
   */
  async getDocument(documentId: string): Promise<{ document: Document }> {
    try {
      const result = await this.request<{ document: Document }>(`/documents/${documentId}`);
      return result;
    } catch (error) {
      // Handle 404 errors gracefully - don't log them as they're expected when documents are deleted
      if (error instanceof Error && error.message.includes('Document not found')) {
        // Re-throw with the same error so DocumentSelectionContext can handle it silently
        throw error;
      }
      // For other errors, let them propagate normally (they'll be logged by request method)
      throw error;
    }
  }

  /**
   * Sube un nuevo documento - Clean Code ≤12 lines
   */
  async uploadDocument(
    file: File,
    useOCR: boolean = true
  ): Promise<{ document: Document; message: string }> {
    logger.info('📄 [API] uploadDocument() called', {
      fileName: file.name,
      fileSize: file.size,
      useOCR,
    });

    try {
      const formData = this._buildUploadFormData(file, useOCR);
      const headers = this._buildUploadHeaders();
      const response = await this._executeUpload(formData, headers);
      return await this._handleUploadResponse(response, file.name);
    } catch (error: any) {
      this._handleUploadError(error, file.name);
      throw error;
    }
  }

  private _buildUploadFormData(file: File, useOCR: boolean): FormData {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('use_ocr', useOCR.toString());
    if (useOCR) {
      formData.append('processing_settings', JSON.stringify({ force_ocr_on_images: true }));
    }
    return formData;
  }

  private _buildUploadHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    const token = this.tokenManager.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  private async _executeUpload(
    formData: FormData,
    headers: Record<string, string>
  ): Promise<Response> {
    return await fetch(`${this.baseUrl}/documents/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
  }

  private async _handleUploadResponse(response: Response, fileName: string): Promise<any> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      const error = new Error(
        errorData.error || errorData.message || `Error ${response.status}: ${response.statusText}`
      );

      if (response.status === 409) {
        logger.info('ℹ️ [API] uploadDocument duplicate detected', {
          fileName,
          existingFile: errorData.existing_document?.name || 'unknown',
        });
      } else {
        logger.error('❌ [API] uploadDocument HTTP error', error);
      }
      throw error;
    }

    const data = await response.json();
    logger.info('📄 [API] uploadDocument success', { documentId: data.document?.id });
    return data;
  }

  private _handleUploadError(error: any, fileName: string): void {
    const isDuplicate =
      error.message?.includes('Duplicate file') || error.message?.includes('already exists');
    if (!isDuplicate) {
      logger.error('❌ [API] uploadDocument failed', error);
    }
  }

  /**
   * Actualiza un documento
   */
  async updateDocument(
    documentId: string,
    updates: { name?: string; company_name?: string; document_period?: string }
  ): Promise<{ success: boolean; document: Document; message: string }> {
    return this.request(`/documents/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Elimina un documento
   */
  async deleteDocument(documentId: string): Promise<{ message: string }> {
    return this.request(`/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Obtiene documentos compartidos (biblioteca pública)
   */
  async getSharedDocuments(filters?: DocumentFilters): Promise<{
    success: boolean;
    documents: DocumentWithSearchResults[];
    count: number;
    search_in_content: boolean;
  }> {
    const joinFilter = (v: string | string[] | undefined): string | undefined => {
      if (!v) return undefined;
      const arr = Array.isArray(v) ? v : [v];
      return arr.length > 0 ? arr.join(',') : undefined;
    };
    const params = new URLSearchParams();
    const companyStr = joinFilter(filters?.company);
    if (companyStr) params.append('company', companyStr);
    const typeStr = joinFilter(filters?.type);
    if (typeStr) params.append('type', typeStr);
    const periodStr = joinFilter(filters?.period);
    if (periodStr) params.append('period', periodStr);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.search_in_content) params.append('search_in_content', 'true');

    const queryString = params.toString();
    return this.request(`/documents/shared${queryString ? '?' + queryString : ''}`);
  }

  /**
   * Obtiene filtros disponibles para documentos compartidos
   */
  async getSharedFilters(): Promise<{
    success: boolean;
    filters: SharedFilters;
  }> {
    return this.request('/documents/shared/filters');
  }

  /**
   * Obtiene sectores con conteo de docs y top empresas
   */
  async getSharedSectors(): Promise<{
    success: boolean;
    sectors: Array<{
      name: string;
      doc_count: number;
      company_count: number;
      top_companies: Array<{ name: string; short_name: string; count: number }>;
    }>;
  }> {
    return this.request('/documents/shared/sectors');
  }

  /**
   * Obtiene empresas con conteo de docs para vista Biblioteca
   */
  async getSharedCompaniesList(): Promise<{
    success: boolean;
    companies: Array<{
      name: string;
      short_name: string;
      sector: string | null;
      doc_count: number;
    }>;
    count: number;
  }> {
    return this.request('/documents/shared/companies-list');
  }

  /**
   * Obtiene los documentos compartidos mas recientes
   */
  async getSharedRecent(limit: number = 20): Promise<{
    success: boolean;
    documents: Document[];
    count: number;
  }> {
    return this.request(`/documents/shared/recent?limit=${limit}`);
  }

  /**
   * Obtiene la selección de documentos del usuario
   */
  async getDocumentSelection(): Promise<DocumentSelectionResponse> {
    try {
      const response = await this.request<DocumentSelectionResponse>('/documents/selection');
      return response;
    } catch (error) {
      logger.error('❌ [API] getDocumentSelection failed', error);
      // Retornar valores por defecto en caso de error
      return {
        success: false,
        selected_document_ids: [],
        filters: {},
      };
    }
  }

  /**
   * Guarda la selección de documentos del usuario - Clean Code ≤12 lines
   */
  async saveDocumentSelection(data: {
    selected_document_ids: string[];
    filters: Record<string, any>;
  }): Promise<DocumentSelectionResponse> {
    try {
      const response = await this.request<DocumentSelectionResponse>('/documents/selection', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      this._logDocumentSelectionSaved(data);
      return response;
    } catch (error) {
      logger.error('❌ [API] saveDocumentSelection failed', error);
      throw error;
    }
  }

  private _logDocumentSelectionSaved(data: {
    selected_document_ids: string[];
    filters: Record<string, any>;
  }): void {
    logger.info('✅ [API] Document selection saved', {
      documentCount: data.selected_document_ids.length,
      filterCount: Object.keys(data.filters).length,
    });
  }

  /**
   * Reprocesa un documento
   */
  async reprocessDocument(documentId: string): Promise<{
    success: boolean;
    document: Document;
    processing: any;
    message: string;
  }> {
    return this.request(`/documents/${documentId}/process?force=true`, {
      method: 'POST',
    });
  }

  /**
   * Procesa un documento
   */
  async processDocument(
    documentId: string,
    force: boolean = false
  ): Promise<{ document: Document; message: string }> {
    const url = force
      ? `/documents/${documentId}/process?force=true`
      : `/documents/${documentId}/process`;
    return this.request(url, {
      method: 'POST',
    });
  }

  /**
   * Obtiene empresas disponibles
   */
  async getCompanies(query?: string): Promise<{
    success: boolean;
    companies: string[];
    count: number;
  }> {
    const params = new URLSearchParams();
    if (query) params.append('q', query);
    const queryString = params.toString();
    return this.request(`/documents/companies${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Obtiene configuración de menciones - Clean Code ≤12 lines
   */
  async getMentionConfig(): Promise<MentionConfigResponse> {
    return this.request('/documents/mentions/config');
  }

  /**
   * Obtiene sugerencias de menciones
   */
  async getMentionSuggestions(
    type:
      | 'empresa'
      | 'tipo'
      | 'fecha'
      | 'sector'
      | 'codigo'
      | 'publicado'
      | 'cargado'
      | 'documento',
    query: string = '',
    options?: RequestInit,
    includeTypes: boolean = false
  ): Promise<MentionSuggestionsResponse> {
    const params = new URLSearchParams();
    params.append('type', type);
    if (query) params.append('q', query);
    if (includeTypes) params.append('include_types', 'true');
    return this.request(`/documents/mentions?${params.toString()}`, options);
  }

  /**
   * Busca archivos
   */
  async searchFiles(query: string, limit: number = 10): Promise<SearchFilesResponse> {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('limit', limit.toString());
    return this.request(`/documents/search-files?${params.toString()}`);
  }

  /**
   * Obtiene un chunk con contexto
   */
  async getChunkWithContext(
    chunkId: string,
    contextSize: number = 1
  ): Promise<ChunkWithContextResponse> {
    return this.request(`/citations/chunk/${chunkId}?context_size=${contextSize}`);
  }

  /**
   * Obtiene chunks de un documento
   */
  async getDocumentChunks(documentId: string, page?: number): Promise<DocumentChunksResponse> {
    const params = page ? `?page=${page}` : '';
    return this.request(`/citations/document/${documentId}/chunks${params}`);
  }

  /**
   * Obtiene tablas de un chunk
   */
  async getChunkTables(chunkId: string): Promise<ChunkTablesResponse> {
    return this.request(`/citations/chunk/${chunkId}/tables`);
  }

  /**
   * Obtiene tablas de un documento
   */
  async getDocumentTables(documentId: string, page?: number): Promise<DocumentTablesResponse> {
    const params = page ? `?page=${page}` : '';
    return this.request(`/citations/document/${documentId}/tables${params}`);
  }

  /**
   * Obtiene payload de Unstructured
   */
  async getUnstructuredPayload(
    documentId: string,
    opts?: { version?: number; include_summary?: boolean }
  ): Promise<any> {
    const params = new URLSearchParams();
    if (opts?.version !== undefined) params.append('version', String(opts.version));
    if (opts?.include_summary) params.append('include_summary', 'true');
    const qs = params.toString();
    return this.request(`/unstructured/${documentId}${qs ? `?${qs}` : ''}`);
  }

  /**
   * Obtiene tablas de Unstructured
   */
  async getUnstructuredTables(
    documentId: string,
    page?: number,
    version?: number
  ): Promise<UnstructuredTablesResponse> {
    const params = new URLSearchParams();
    if (page !== undefined) params.append('page', String(page));
    if (version !== undefined) params.append('version', String(version));
    const qs = params.toString();
    return this.request(`/unstructured/${documentId}/tables${qs ? `?${qs}` : ''}`);
  }
}
